/**
 * Low-level Meta Pixel (fbq) loader and tracker.
 *
 * Browser-only: loads Meta's native fbevents.js and forwards standard events
 * straight to Meta. Meta automatically captures device, browser, screen
 * resolution, geolocation (via IP), referrer and URL/UTM parameters on every
 * event, so we only send the event-specific commerce data (value, currency,
 * content ids). There is no Conversions API token and no server-side delivery.
 */

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fbq?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _fbq?: any;
  }
}

let initializedPixelId: string | null = null;

function loadBase(): void {
  if (window.fbq) return;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const n: any = function (...args: unknown[]) {
    if (n.callMethod) {
      n.callMethod(...args);
    } else {
      n.queue.push(args);
    }
  };
  n.queue = [];
  n.loaded = true;
  n.version = "2.0";
  n.push = n;
  window.fbq = n;
  if (!window._fbq) window._fbq = n;

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  const firstScript = document.getElementsByTagName("script")[0];
  firstScript?.parentNode?.insertBefore(script, firstScript);
}

export function initMetaPixel(pixelId: string): void {
  if (initializedPixelId === pixelId) return;
  loadBase();
  window.fbq?.("init", pixelId);
  initializedPixelId = pixelId;
  window.fbq?.("track", "PageView");
}

export function isMetaPixelReady(): boolean {
  return initializedPixelId !== null && typeof window.fbq === "function";
}

export function trackMetaPixel(
  event: string,
  params?: Record<string, unknown>,
  options?: { eventID?: string },
): void {
  if (!isMetaPixelReady()) return;
  if (options?.eventID) {
    window.fbq?.("track", event, params ?? {}, { eventID: options.eventID });
    return;
  }
  window.fbq?.("track", event, params ?? {});
}
